import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ITask, isTaskAssignedToTeam } from 'shared';

import { ISocketEvent } from '../../../models/socket-event.model';
import { AppStateService } from '../state';
import { TaskService } from '../task.service';
import { TeamService } from '../team.service';
import { UserService } from '../user.service';
import { BaseSocketEventHandler } from './base-socket-event-handler';

@Injectable({ providedIn: 'root' })
export abstract class TaskAssignmentBaseSocketEventHandler<T extends ISocketEvent> extends BaseSocketEventHandler<T> {
    private readonly taskService = inject(TaskService);
    private readonly userService = inject(UserService);
    private readonly teamService = inject(TeamService);
    protected readonly appState = inject(AppStateService);

    protected async ensureLocalState(event: T): Promise<boolean> {
        const hasPermission = await firstValueFrom(this.canSeeTask(event.task));
        if (hasPermission) this.taskService.localUpdate(event.task);
        else this.taskService.localRemove(event.task.id);
        return hasPermission;
    }

    protected canSeeTask(task: ITask): Observable<boolean> {
        return combineLatest([
            this.appState.user.distinctStream,
            this.userService.hasPermission('Tasks.ReadTasks.All'),
            this.userService.hasPermission('Tasks.ReadTasks.Self'),
            this.userService.hasPermission('Tasks.ReadTasks.Teams'),
            this.userService.hasPermission('Tasks.ReadTasks.Unassigned'),
            this.teamService.myTeamIds$,
        ]).pipe(
            map(
                ([user, readAll, readSelf, readTeams, readUnassigned, myTeamIds]) =>
                    readAll ||
                    (readSelf && !!task.assigneeId && task.assigneeId === user.id) ||
                    (readTeams && isTaskAssignedToTeam(task, myTeamIds)) ||
                    (readUnassigned && !task.assigneeId)
            )
        );
    }
}
